import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  TablePagination,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
} from '@mui/material';
import { hasWritePermission } from '../../utils/menuFilter';
import {
  Search,
  Pause,
  Home,
  AttachMoney,
  Business,
  CheckCircle,
  Warning,
  LocationOn,
} from '@mui/icons-material';

const HoldPlot = () => {
  const canWriteHoldPlot = hasWritePermission('plot-booking.hold-plot');
  const [searchForm, setSearchForm] = useState({
    site: '',
    sector: '',
    block: '',
  });

  const [showPlotDetails, setShowPlotDetails] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const [holdDialogOpen, setHoldDialogOpen] = useState(false);
  const [selectedPlot, setSelectedPlot] = useState(null);
  const [holdForm, setHoldForm] = useState({
    holdReason: '',
    holdDuration: '',
    remarks: ''
  });
  const [errors, setErrors] = useState({});
  const [searchLoading, setSearchLoading] = useState(false);
  const [holdLoading, setHoldLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Sample plot data - this would come from API
  const [plotData] = useState([
    {
      id: 1,
      plotNo: 'P101',
      siteName: 'Green Valley',
      sectorName: 'Sector A',
      blockName: 'Block 1',
      plc: '₹2,50,000',
      plotDimension: '30x40 ft',
      status: 'Available',
    },
    {
      id: 2,
      plotNo: 'P102',
      siteName: 'Green Valley',
      sectorName: 'Sector A',
      blockName: 'Block 1',
      plc: '₹2,75,000',
      plotDimension: '25x50 ft',
      status: 'Hold',
    },
    {
      id: 3,
      plotNo: 'P205',
      siteName: 'Hill View',
      sectorName: 'Sector B',
      blockName: 'Block 2',
      plc: '₹3,00,000',
      plotDimension: '35x45 ft',
      status: 'Booked',
    },
    {
      id: 4,
      plotNo: 'P321',
      siteName: 'Paradise Heights',
      sectorName: 'Sector C',
      blockName: 'Block 3',
      plc: '₹4,50,000',
      plotDimension: '40x50 ft',
      status: 'Available',
    },
    {
      id: 5,
      plotNo: 'P456',
      siteName: 'Royal Gardens',
      sectorName: 'Sector D',
      blockName: 'Block 4',
      plc: '₹5,00,000',
      plotDimension: '45x55 ft',
      status: 'Hold',
    },
    {
      id: 6,
      plotNo: 'P087',
      siteName: 'Green Valley',
      sectorName: 'Sector A',
      blockName: 'Block 1',
      plc: '₹2,25,000',
      plotDimension: '28x42 ft',
      status: 'Available',
    },
    {
      id: 7,
      plotNo: 'P789',
      siteName: 'Green Valley',
      sectorName: 'Sector B',
      blockName: 'Block 2',
      plc: '₹3,25,000',
      plotDimension: '32x48 ft',
      status: 'Booked',
    },
    {
      id: 8,
      plotNo: 'P654',
      siteName: 'Hill View',
      sectorName: 'Sector B',
      blockName: 'Block 2',
      plc: '₹3,50,000',
      plotDimension: '38x52 ft',
      status: 'Available',
    },
    {
      id: 9,
      plotNo: 'P432',
      siteName: 'Paradise Heights',
      sectorName: 'Sector C',
      blockName: 'Block 3',
      plc: '₹4,75,000',
      plotDimension: '42x58 ft',
      status: 'Hold',
    },
    {
      id: 10,
      plotNo: 'P567',
      siteName: 'Royal Gardens',
      sectorName: 'Sector D',
      blockName: 'Block 4',
      plc: '₹5,25,000',
      plotDimension: '48x60 ft',
      status: 'Available',
    },
    {
      id: 11,
      plotNo: 'P890',
      siteName: 'Green Valley',
      sectorName: 'Sector A',
      blockName: 'Block 1',
      plc: '₹2,00,000',
      plotDimension: '25x40 ft',
      status: 'Booked',
    },
    {
      id: 12,
      plotNo: 'P123',
      siteName: 'Hill View',
      sectorName: 'Sector B',
      blockName: 'Block 2',
      plc: '₹3,75,000',
      plotDimension: '40x55 ft',
      status: 'Hold',
    },
  ]);

  // Sample dropdown options
  const siteOptions = ['Green Valley', 'Hill View', 'Paradise Heights', 'Royal Gardens'];
  const sectorOptions = ['Sector A', 'Sector B', 'Sector C', 'Sector D'];
  const blockOptions = ['Block 1', 'Block 2', 'Block 3', 'Block 4'];

  const handleInputChange = (field, value) => {
    setSearchForm({
      ...searchForm,
      [field]: value,
    });
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSearch = async () => {
    setSearchLoading(true);
    setErrors({});

    // Simulate API call
    setTimeout(() => {
      setShowPlotDetails(true);
      setPage(0); // Reset to first page when searching
      console.log('Searching with criteria:', searchForm);
      setSuccessMessage('');
      setSearchLoading(false);
    }, 1500);
  };

  const handleClearSearch = () => {
    setSearchForm({
      site: '',
      sector: '',
      block: '',
    });
    setShowPlotDetails(true); // Keep showing all plots after clear
    setPage(0); // Reset to first page when clearing
  };

  const handleHoldPlot = (plot) => {
    if (plot.status !== 'Available') {
      setErrors({ general: 'Only available plots can be put on hold' });
      return;
    }
    setSelectedPlot(plot);
    setHoldDialogOpen(true);
  };

  const handleCloseHoldDialog = () => {
    setHoldDialogOpen(false);
    setSelectedPlot(null);
    setHoldForm({
      holdReason: '',
      holdDuration: '',
      remarks: ''
    });
    setErrors({});
  };

  const handleHoldFormChange = (e) => {
    const { name, value } = e.target;
    setHoldForm({ ...holdForm, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateHoldForm = () => {
    const newErrors = {};

    if (!holdForm.holdReason.trim()) {
      newErrors.holdReason = 'Hold reason is required';
    }

    if (!holdForm.holdDuration.trim()) {
      newErrors.holdDuration = 'Hold duration is required';
    }

    return newErrors;
  };

  const handleConfirmHold = async () => {
    const validationErrors = validateHoldForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setHoldLoading(true);

    // Simulate API call
    setTimeout(() => {
      setSuccessMessage(`Plot ${selectedPlot.plotNo} has been successfully put on hold`);
      setHoldDialogOpen(false);
      setHoldForm({
        holdReason: '',
        holdDuration: '',
        remarks: ''
      });
      setSelectedPlot(null);
      setErrors({});
      console.log('Plot held:', {
        plot: selectedPlot,
        holdDetails: holdForm
      });
      setHoldLoading(false);
    }, 2000);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // Enhanced filter function for plot data
  const getFilteredPlotData = () => {
    if (!showPlotDetails) return [];

    let filteredData = plotData;

    // Filter by search form criteria
    if (searchForm.site) {
      filteredData = filteredData.filter(plot => plot.siteName === searchForm.site);
    }
    if (searchForm.sector) {
      filteredData = filteredData.filter(plot => plot.sectorName === searchForm.sector);
    }
    if (searchForm.block) {
      filteredData = filteredData.filter(plot => plot.blockName === searchForm.block);
    }

    // Filter by table search query
    if (tableSearchQuery.trim()) {
      filteredData = filteredData.filter(plot =>
        Object.values(plot).some(value =>
          String(value).toLowerCase().includes(tableSearchQuery.toLowerCase())
        )
      );
    }

    return filteredData;
  };

  // Get search summary for display
  const getSearchSummary = () => {
    const criteria = [];
    if (searchForm.site) criteria.push(`Site: "${searchForm.site}"`);
    if (searchForm.sector) criteria.push(`Sector: "${searchForm.sector}"`);
    if (searchForm.block) criteria.push(`Block: "${searchForm.block}"`);

    return criteria.length > 0 ? criteria.join(', ') : 'All Plots';
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Available':
        return {
          backgroundColor: '#4caf50',
          color: 'white',
        };
      case 'Hold':
        return {
          backgroundColor: '#ff9800',
          color: 'white',
        };
      case 'Booked':
        return {
          backgroundColor: '#f44336',
          color: 'white',
        };
      default:
        return {};
    }
  };

  // Calculate statistics
  const totalPlots = plotData.length;
  const availableCount = plotData.filter(plot => plot.status === 'Available').length;
  const holdCount = plotData.filter(plot => plot.status === 'Hold').length;
  const bookedCount = plotData.filter(plot => plot.status === 'Booked').length;
  const totalValue = plotData.reduce((sum, plot) => {
    const amount = parseFloat(plot.plc.replace(/[₹,]/g, ''));
    return sum + amount;
  }, 0);

  const holdReasons = [
    'Customer Request',
    'Payment Pending',
    'Documentation Issues',
    'Legal Issues',
    'Maintenance Required',
    'Other',
  ];

  const holdDurations = [
    '7 Days',
    '15 Days',
    '30 Days',
    '60 Days',
    '90 Days',
    'Custom',
  ];

  return (
    <Box sx={{ p: 3, backgroundColor: '#fafafa', minHeight: 'auto' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
          Hold Plot
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Search and manage plot holds for maintenance and customer requests
        </Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle />
            {successMessage}
          </Box>
        </Alert>
      )}

      {errors.general && (
        <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Warning />
            {errors.general}
          </Box>
        </Alert>
      )}

      {/* Summary Cards */}
      {/* <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: '#e3f2fd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Home sx={{ color: '#1976d2', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                    {totalPlots}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Plots
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: '#e8f5e9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <CheckCircle sx={{ color: '#2e7d32', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                    {availableCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Available Plots
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: '#fff3e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Pause sx={{ color: '#f57c00', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                    {holdCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    On Hold Plots
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: '#ffebee',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <AttachMoney sx={{ color: '#f44336', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                    ₹{(totalValue / 100000).toFixed(1)}L
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Value
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid> */}

      {/* Search Section */}
      <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Search sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
              Search Plot Status Details
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Site</InputLabel>
                <Select
                  value={searchForm.site}
                  label="Site"
                  onChange={(e) => handleInputChange('site', e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Select Site</MenuItem>
                  {siteOptions.map((site) => (
                    <MenuItem key={site} value={site}>
                      {site}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Sector</InputLabel>
                <Select
                  value={searchForm.sector}
                  label="Sector"
                  onChange={(e) => handleInputChange('sector', e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Select Sector</MenuItem>
                  {sectorOptions.map((sector) => (
                    <MenuItem key={sector} value={sector}>
                      {sector}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Block</InputLabel>
                <Select
                  value={searchForm.block}
                  label="Block"
                  onChange={(e) => handleInputChange('block', e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Select Block</MenuItem>
                  {blockOptions.map((block) => (
                    <MenuItem key={block} value={block}>
                      {block}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearch}
                  disabled={searchLoading}
                  startIcon={<Search />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '15px',
                    boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                    borderRadius: 2
                  }}
                >
                  {searchLoading ? 'Searching...' : 'Search Plots'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleClearSearch}
                  sx={{
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2
                  }}
                >
                  Clear & Show All
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Plot Status Details Table */}
      {showPlotDetails && (
        <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Box display="flex" alignItems="center" gap={2}>
                <Pause sx={{ color: '#f57c00', fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                  Plot Status Details
                </Typography>
              </Box>
              <TextField
                label="Search Plot..."
                variant="outlined"
                size="small"
                value={tableSearchQuery}
                onChange={(e) => setTableSearchQuery(e.target.value)}
                sx={{
                  width: 300,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Search Criteria: {getSearchSummary()}
            </Typography>
            <Typography variant="body2" color="primary" sx={{ mb: 3, fontWeight: 600 }}>
              Found {getFilteredPlotData().length} plot(s) matching your search criteria
            </Typography>

            {getFilteredPlotData().length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>S.No</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Plot Details</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Location</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Financial Details</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Status</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getFilteredPlotData()
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((plot, index) => (
                        <TableRow
                          key={plot.id}
                          sx={{
                            '&:hover': { backgroundColor: '#f9fafb' },
                            '& td': { py: 2 }
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600, color: '#000' }}>
                            {page * rowsPerPage + index + 1}
                          </TableCell>

                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#000' }}>
                                Plot {plot.plotNo}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {plot.plotDimension}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#000' }}>
                                {plot.siteName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {plot.sectorName}, {plot.blockName}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                              PLC: {plot.plc}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={plot.status}
                              sx={{ ...getStatusStyle(plot.status), fontWeight: 600, fontSize: '12px' }}
                              size="small"
                            />
                          </TableCell>

                          <TableCell align="center">
                            {plot.status === 'Available' ? (
                              // canWriteHoldPlot && (
                              <Tooltip title="Hold Plot">
                                <IconButton
                                  color="warning"
                                  onClick={() => handleHoldPlot(plot)}
                                  sx={{
                                    '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.1)' }
                                  }}
                                >
                                  <Pause />
                                </IconButton>
                              </Tooltip>
                              // )
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                {plot.status}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                <TablePagination
                  
                  component="div"
                  count={getFilteredPlotData().length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{ borderTop: '1px solid #e0e0e0' }}
                />
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Alert severity="info">
                  <Typography variant="h6" gutterBottom>
                    No Plot Details Found
                  </Typography>
                  <Typography variant="body2">
                    No plots match your search criteria: {getSearchSummary()}
                  </Typography>
                </Alert>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Hold Plot Dialog */}
      <Dialog
        open={holdDialogOpen}
        onClose={handleCloseHoldDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          fontSize: '24px',
          fontWeight: 700,
          color: '#000',
          borderBottom: '1px solid #e0e0e0',
          pb: 2
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Pause sx={{ color: '#f57c00', fontSize: 28 }} />
            Hold Plot
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedPlot && (
            <Box>
              <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Put plot {selectedPlot.plotNo} on hold
                </Typography>
              </Alert>

              {/* Plot Information */}
              <Card sx={{ p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                  Plot Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Plot Number</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedPlot.plotNo}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Plot Dimension</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedPlot.plotDimension}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Site Location</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedPlot.siteName}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Sector & Block</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedPlot.sectorName}, {selectedPlot.blockName}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">PLC Amount</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedPlot.plc}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Current Status</Typography>
                    <Chip
                      label={selectedPlot.status}
                      color="success"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Grid>
                </Grid>
              </Card>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">Hold Details</Typography>
              </Divider>

              {/* Hold Form */}
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.holdReason}>
                    <InputLabel>Hold Reason *</InputLabel>
                    <Select
                      name="holdReason"
                      value={holdForm.holdReason}
                      label="Hold Reason *"
                      onChange={handleHoldFormChange}
                      sx={{ borderRadius: 2 }}
                    >
                      {holdReasons.map((reason) => (
                        <MenuItem key={reason} value={reason}>
                          {reason}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.holdReason && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {errors.holdReason}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.holdDuration}>
                    <InputLabel>Hold Duration *</InputLabel>
                    <Select
                      name="holdDuration"
                      value={holdForm.holdDuration}
                      label="Hold Duration *"
                      onChange={handleHoldFormChange}
                      sx={{ borderRadius: 2 }}
                    >
                      {holdDurations.map((duration) => (
                        <MenuItem key={duration} value={duration}>
                          {duration}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.holdDuration && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {errors.holdDuration}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="remarks"
                    label="Additional Remarks"
                    value={holdForm.remarks}
                    onChange={handleHoldFormChange}
                    placeholder="Enter any additional remarks or notes"
                    multiline
                    rows={3}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={handleCloseHoldDialog}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Cancel
          </Button>
          {/* {canWriteHoldPlot && ( */}
          <Button
            onClick={handleConfirmHold}
            color="warning"
            variant="contained"
            disabled={!holdForm.holdReason || !holdForm.holdDuration || holdLoading}
            startIcon={<Pause />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)'
            }}
          >
            {holdLoading ? 'Putting on Hold...' : 'Hold Plot'}
          </Button>
          {/* )} */}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HoldPlot;